import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_COLORS, relTime } from "@/lib/orgUtils";
import { Search, Plus, Trash2, UserX, UserCheck, Lock, X, Pencil } from "lucide-react";

const Kpi = ({ label, value, accent, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} disabled={!onClick}
    className={`text-left p-4 rounded-xl bg-white border border-[#181B34]/10 shadow-sm min-w-36 ${onClick ? "hover:border-[#6161FF] cursor-pointer" : "cursor-default"}`}
    style={accent ? { borderTop: `4px solid ${accent}` } : {}}>
    <p className="text-3xl font-black">{value}</p>
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mt-1">{label}</p>
  </button>
);

export const RoleBadge = ({ role }) => {
  const rc = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: rc.bg, color: rc.text }}>{rc.label}</span>;
};

export default function UserManagement() {
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], kpis: {} });
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });

  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => data.users.filter((u) =>
    (!q || u.email.includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase())) &&
    (!roleFilter || u.role === roleFilter) && (!anomalyOnly || u.anomaly)
  ), [data.users, q, roleFilter, anomalyOnly]);

  const addUser = async () => {
    try {
      await api.post("/users", form);
      toast.success("User invited — they can sign in with OTP");
      setAddOpen(false);
      setForm({ email: "", name: "", role: "viewer" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/users/${editUser.id}`, { name: editForm.name, email: editForm.email });
      toast.success("User details updated");
      setEditUser(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const roles = user.role === "super_admin" ? ["super_admin", "admin", "editor", "viewer"] : ["admin", "editor", "viewer"];

  return (
    <div>
      <div className="flex gap-3 flex-wrap">
        <Kpi label="Total Users" value={data.kpis.total ?? "—"} testid="kpi-total-users" />
        <Kpi label="Active Users" value={data.kpis.active ?? "—"} accent="#00C875" testid="kpi-active-users" />
        <Kpi label="Editors" value={data.kpis.editors ?? "—"} accent="#FDAB3D" testid="kpi-editors" />
        <Kpi label="Viewers Linked" value={data.kpis.viewers_linked ?? "—"} accent="#579BFC" testid="kpi-viewers-linked" />
        <Kpi label="Access Health" value={data.kpis.access_health ?? "—"} accent="#E2445C" testid="kpi-access-health"
          onClick={() => setAnomalyOnly(!anomalyOnly)} />
      </div>
      <div className="mt-5 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-56">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
          <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…"
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6161FF]/25" />
        </div>
        <select data-testid="users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold">
          <option value="">All roles</option>
          {["super_admin", "admin", "editor", "viewer"].map((r) => <option key={r} value={r}>{ROLE_COLORS[r].label}</option>)}
        </select>
        {anomalyOnly && <button onClick={() => setAnomalyOnly(false)} className="h-10 px-3 rounded-xl bg-[#E2445C]/10 text-[#E2445C] text-xs font-bold">Anomalies only ✕</button>}
        <button data-testid="add-user-btn" onClick={() => setAddOpen(true)} className="ml-auto flex items-center gap-2 px-5 h-10 rounded-xl bg-[#6161FF] text-white font-bold text-sm">
          <Plus size={15} /> Add user
        </button>
      </div>
      <div className="mt-4 bg-white rounded-2xl border border-[#181B34]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Linked Employee</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last Login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSA = u.role === "super_admin";
              const locked = isSA && user.role !== "super_admin";
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-[#181B34]/5 ${u.anomaly ? "bg-[#E2445C]/4" : ""}`}>
                  <td className="px-4 py-2.5 font-bold">{u.name || "—"}</td>
                  <td className="px-4 py-2.5 font-semibold text-[#4b4e63]">{u.email}</td>
                  <td className="px-4 py-2.5">
                    {locked ? <span className="flex items-center gap-1"><RoleBadge role={u.role} /><Lock size={11} className="text-[#9699AD]" /></span> :
                      <select data-testid={`user-role-select-${u.email}`} value={u.role} onChange={(e) => act(() => api.put(`/users/${u.id}`, { role: e.target.value }), "Role updated")}
                        className="text-xs font-bold bg-transparent border border-[#181B34]/10 rounded-lg px-1.5 py-1">
                        {(isSA ? ["super_admin", ...roles.filter((r) => r !== "super_admin")] : roles).map((r) => <option key={r} value={r}>{ROLE_COLORS[r].label}</option>)}
                      </select>}
                  </td>
                  <td className="px-4 py-2.5 font-semibold text-[#676a82]">{u.linked_employee || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-[#00C875]/15 text-[#00925a]" : u.status === "invited" ? "bg-[#FDAB3D]/15 text-[#c97a10]" : "bg-[#181B34]/8 text-[#676a82]"}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-semibold text-[#676a82]">{u.last_login ? relTime(u.last_login) : "Never"}</td>
                  <td className="px-4 py-2.5 text-right">
                    {!locked && (
                      <button title="Edit details" data-testid={`edit-user-${u.email}`}
                        onClick={() => { setEditUser(u); setEditForm({ name: u.name || "", email: u.email }); }}
                        className="w-7 h-7 rounded-lg hover:bg-[#F0F3FF] text-[#676a82] inline-flex items-center justify-center"><Pencil size={13} /></button>
                    )}
                    {!locked && u.id !== user.id && (
                      <>
                        <button title={u.status === "inactive" ? "Reactivate" : "Deactivate"} data-testid={`toggle-status-${u.email}`}
                          onClick={() => act(() => api.put(`/users/${u.id}`, { status: u.status === "inactive" ? "active" : "inactive" }), u.status === "inactive" ? "Reactivated" : "Deactivated")}
                          className="w-7 h-7 rounded-lg hover:bg-[#F0F3FF] text-[#676a82] inline-flex items-center justify-center">
                          {u.status === "inactive" ? <UserCheck size={13} /> : <UserX size={13} />}
                        </button>
                        <button title="Delete" data-testid={`delete-user-${u.email}`}
                          onClick={() => window.confirm(`Delete ${u.email}?`) && act(() => api.delete(`/users/${u.id}`), "User deleted")}
                          className="w-7 h-7 rounded-lg hover:bg-[#E2445C]/10 text-[#676a82] hover:text-[#E2445C] inline-flex items-center justify-center"><Trash2 size={13} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editUser && (
        <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-user-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black">Edit user details</h3>
              <button onClick={() => setEditUser(null)}><X size={17} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Name</label>
                <input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Email (login identity)</label>
                <input data-testid="edit-user-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
                <p className="mt-1 text-[11px] text-[#9699AD] font-medium">Changing the email updates their OTP sign-in address and re-links them to any matching employee record.</p>
              </div>
              <button data-testid="edit-user-save" onClick={saveEdit} className="w-full h-11 rounded-xl bg-[#6161FF] text-white font-bold">Save details</button>
            </div>
          </div>
        </div>
      )}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setAddOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="add-user-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black">Invite user</h3>
              <button onClick={() => setAddOpen(false)}><X size={17} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="add-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
                className="w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
              <input data-testid="add-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (optional)"
                className="w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
              <select data-testid="add-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-11 px-2 rounded-xl border border-[#181B34]/15 font-bold bg-white">
                {roles.map((r) => <option key={r} value={r}>{ROLE_COLORS[r].label}</option>)}
              </select>
              <button data-testid="add-user-submit" onClick={addUser} className="w-full h-11 rounded-xl bg-[#6161FF] text-white font-bold">Send invite (OTP login)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
